import React, { useEffect, useMemo, useRef, useState } from 'react';
import { onValue, ref, set } from 'firebase/database';
import { database } from '../firebase';

type PromptScope = 'preview' | 'standard' | 'global';
type ImageScale = '1:1' | '2:3' | '3:2' | '3:4' | '4:3' | '4:5' | '5:4' | '9:16' | '16:9' | '21:9';
type ImageResolution = '512' | '1K' | '2K' | '4K';
type ThinkingLevel = 'minimal' | 'low' | 'medium' | 'high';
type ThinkingLevelOption = 'default' | ThinkingLevel;

type PromptTemplate = {
  id: number;
  nombre: string;
  archivo: string;
  model: string;
  thinking_level?: ThinkingLevel | null;
  enable_web_search?: boolean;
  image_scale?: ImageScale;
  image_resolution?: ImageResolution;
  is_pdf: boolean;
  is_logo: boolean;
  inputs: string[];
  outputs: string[];
  inputs_img: string[];
  prompt: string;
};

type PricingItem = {
  model_id?: string;
};

type BlueUsageState = {
  consumerMap: Map<string, Set<number>>;
  blueOnlyTemplateMap: boolean[];
};

type EditorState =
  | {
      mode: 'create';
      index: number;
    }
  | {
      mode: 'edit';
      index: number;
    }
  | null;

type EditorDraft = {
  nombre: string;
  archivoBase: string;
  model: string;
  thinkingLevel: ThinkingLevelOption;
  enableWebSearch: boolean;
  imageScale: ImageScale;
  imageResolution: ImageResolution;
  isPdf: boolean;
  isLogo: boolean;
  prompt: string;
  inputs_img: string[];
  outputs: string[];
};

const scopePathMap: Record<PromptScope, string> = {
  preview: 'PromptsPreview',
  standard: 'PromptsStandard',
  global: 'PromptsGlobal',
};

const scopeLabelMap: Record<PromptScope, string> = {
  preview: 'Preview',
  standard: 'Standard',
  global: 'Global',
};
const imageScaleOptions: ImageScale[] = ['1:1', '2:3', '3:2', '3:4', '4:3', '4:5', '5:4', '9:16', '16:9', '21:9'];
const imageResolutionOptions: ImageResolution[] = ['512', '1K', '2K', '4K'];
const thinkingLevelOptions: ThinkingLevel[] = ['minimal', 'low', 'medium', 'high'];
const reservedFileNames = new Set(['cuestionario.json', 'preview.json', 'manual_overrides.json']);
const pdfOnlyFlowItemList = [
  'brand_name',
  'color_1_hex',
  'color_2_hex',
  'color_3_hex',
  'color_4_hex',
  'brand_concept_traducida',
  'brand_concept_verbal',
  'brand_expand',
  'who_we_are',
  'who_we_are_expand',
  'mission',
  'mission_expand',
  'vision',
  'vision_expand',
  'manifest',
  'manifest_expand',
  'storytelling',
  'storytelling_expand',
  'values',
  'values_expand',
  'color_guidelines',
  'color_expand',
  'typography_title',
  'typography_title_expand',
  'typography_text',
  'typography_text_expand',
  'how_isotype_traducida',
  'isotype_case',
  'isotype_expand',
  'how_logotype_traducida',
  'logotype_case',
  'logotype_expand',
  'how_graphic_line_traducida',
  'graphic_line_expand',
  'industry_expand',
  'target_profile',
  'target_profile_expand',
  'market_strategy',
  'market_strategy_expand',
  'value_proposition',
  'value_proposition_expand',
  'behavioral_strategy',
  'behavioral_strategy_expand',
  'action_plan_90d',
  'isotype.png',
];
const pdfOnlyFlowItems = new Set(pdfOnlyFlowItemList);
const globalOnlyPdfFlowItems = new Set([
  'target_profile',
  'target_profile_expand',
  'market_strategy',
  'market_strategy_expand',
  'value_proposition',
  'value_proposition_expand',
  'behavioral_strategy',
  'behavioral_strategy_expand',
  'action_plan_90d',
]);
const flowQuestionItems = [
  'F1Q1',
  'F1Q2',
  'F1Q3',
  'F1Q4',
  'F2Q5',
  'F2Q6',
  'F3Q7_dominant',
  'F3Q7_matiz',
  'F3Q9',
  'F4Q10',
  'F4Q11',
  'F4Q12',
  'F4Q13',
  'F4Q14',
];
const hardcodedFlowOutputsByScope: Record<PromptScope, string[]> = {
  preview: [
    'color_1_name',
    'color_1_hex',
    'color_2_name',
    'color_2_hex',
    'color_3_name',
    'color_3_hex',
    'color_4_name',
    'color_4_hex',
    'manifest',
    'color_guidelines',
    'how_isotype_traducida',
    'how_logotype_traducida',
    'logotype_preview.png',
    'isotype_preview.png',
    'mockups1.png',
    'mockups2.png',
  ],
  standard: [
    'LANGUAGE',
    ...pdfOnlyFlowItemList.filter((item) => !globalOnlyPdfFlowItems.has(item)),
  ],
  global: ['LANGUAGE', ...pdfOnlyFlowItemList],
};

const extractPromptVariables = (template: string) => {
  const regex = /\{([A-Za-z0-9_]+)\}/g;
  const unique = new Set<string>();
  let match = regex.exec(template);
  while (match) {
    const rawKey = match[1];
    if (rawKey) {
      unique.add(rawKey);
    }
    match = regex.exec(template);
  }
  return Array.from(unique);
};

const parseRtdbTemplates = (value: Record<string, PromptTemplate> | null): PromptTemplate[] => {
  if (!value) {
    return [];
  }

  return Object.entries(value)
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([key, item]) => ({
      id: Number(item?.id ?? Number(key)),
      nombre: item?.nombre || '',
      archivo: item?.archivo || '',
      model: item?.model || '',
      thinking_level: (item?.thinking_level as ThinkingLevel | null | undefined) ?? null,
      enable_web_search: Boolean(item?.enable_web_search),
      image_scale: item?.image_scale,
      image_resolution: item?.image_resolution,
      is_pdf: Boolean(item?.is_pdf),
      is_logo: Boolean(item?.is_logo),
      inputs: Array.isArray(item?.inputs) ? item.inputs : [],
      outputs: Array.isArray(item?.outputs) ? item.outputs : [],
      inputs_img: Array.isArray(item?.inputs_img) ? item.inputs_img : [],
      prompt: item?.prompt || '',
    }));
};

const sanitizeArchivoBase = (value: string) => value.toLowerCase().replace(/[^a-z0-9_]/g, '');

const getDisplayedTemplateOutputs = (template: PromptTemplate) =>
  (template.archivo.toLowerCase().endsWith('.png')
    ? [...template.outputs, template.archivo]
    : template.outputs
  )
    .map((item) => item.trim())
    .filter((item) => item.length > 0);

const buildBlueUsageState = (items: PromptTemplate[]): BlueUsageState => {
  const consumerMap = new Map<string, Set<number>>();
  const outputSets = items.map((template) => new Set(getDisplayedTemplateOutputs(template)));
  const outputCountMap = new Map<string, number>();

  outputSets.forEach((outputSet) => {
    outputSet.forEach((output) => {
      outputCountMap.set(output, (outputCountMap.get(output) || 0) + 1);
    });
  });
  const repeatedOutputs = new Set(
    Array.from(outputCountMap.entries())
      .filter(([, count]) => count > 1)
      .map(([output]) => output)
  );

  items.forEach((template, index) => {
    [...template.inputs, ...template.inputs_img]
      .map((item) => item.trim())
      .filter((item) => item.length > 0)
      .forEach((item) => {
        const consumers = consumerMap.get(item) || new Set<number>();
        consumers.add(index);
        consumerMap.set(item, consumers);
      });
  });

  const blueOutputs = new Set<string>();
  outputSets.forEach((outputSet) => {
    outputSet.forEach((output) => {
      if (!consumerMap.has(output) && pdfOnlyFlowItems.has(output)) {
        blueOutputs.add(output);
      }
    });
  });

  let blueOnlyTemplateMap = items.map(() => false);
  let hasChanges = true;
  while (hasChanges) {
    hasChanges = false;
    blueOnlyTemplateMap = outputSets.map(
      (outputSet) =>
        outputSet.size > 0 &&
        Array.from(outputSet).every(
          (output) => blueOutputs.has(output) && !repeatedOutputs.has(output)
        )
    );

    outputSets.forEach((outputSet) => {
      outputSet.forEach((output) => {
        if (blueOutputs.has(output)) {
          return;
        }
        const consumers = consumerMap.get(output);
        if (
          consumers?.size &&
          Array.from(consumers).every((index) => blueOnlyTemplateMap[index])
        ) {
          blueOutputs.add(output);
          hasChanges = true;
        }
      });
    });
  }

  blueOnlyTemplateMap = outputSets.map(
    (outputSet) =>
      outputSet.size > 0 &&
      Array.from(outputSet).every(
        (output) => blueOutputs.has(output) && !repeatedOutputs.has(output)
      )
  );

  return { consumerMap, blueOnlyTemplateMap };
};

function BrandGenerationSection() {
  const [scope, setScope] = useState<PromptScope>('preview');
  const [templates, setTemplates] = useState<PromptTemplate[]>([]);
  const [previewTemplates, setPreviewTemplates] = useState<PromptTemplate[]>([]);
  const [standardTemplates, setStandardTemplates] = useState<PromptTemplate[]>([]);
  const [globalTemplates, setGlobalTemplates] = useState<PromptTemplate[]>([]);
  const [status, setStatus] = useState('');
  const [statusType, setStatusType] = useState<'neutral' | 'success' | 'error'>('neutral');
  const statusTimer = useRef<number | null>(null);

  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [modelsStatus, setModelsStatus] = useState('Cargando modelos...');

  const [editor, setEditor] = useState<EditorState>(null);
  const [draft, setDraft] = useState<EditorDraft>({
    nombre: '',
    archivoBase: '',
    model: '',
    thinkingLevel: 'default',
    enableWebSearch: false,
    imageScale: '1:1',
    imageResolution: '1K',
    isPdf: false,
    isLogo: false,
    prompt: '',
    inputs_img: [],
    outputs: [],
  });
  const [listInputImgDraft, setListInputImgDraft] = useState('');
  const [listOutputDraft, setListOutputDraft] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const activePath = scopePathMap[scope];

  useEffect(() => {
    const pricingRef = ref(database, 'Pricing');
    setModelsStatus('Cargando modelos...');
    const unsubscribe = onValue(
      pricingRef,
      (snapshot) => {
        const value = snapshot.val() as Record<string, PricingItem> | null;
        if (!value) {
          setAvailableModels([]);
          setModelsStatus('No hay modelos configurados.');
          return;
        }

        const models = Object.entries(value)
          .map(([key, item]) => item.model_id || key)
          .filter((model) => model.trim().length > 0);
        const uniqueModels = Array.from(new Set(models));
        setAvailableModels(uniqueModels);
        setModelsStatus('');
        setDraft((prev) => {
          if (prev.model || uniqueModels.length === 0) {
            return prev;
          }
          return {
            ...prev,
            model: uniqueModels[0],
          };
        });
      },
      () => {
        setAvailableModels([]);
        setModelsStatus('Error al cargar modelos.');
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const promptsRef = ref(database, activePath);
    const unsubscribe = onValue(
      promptsRef,
      (snapshot) => {
        const value = snapshot.val() as Record<string, PromptTemplate> | null;
        setTemplates(parseRtdbTemplates(value));
      },
      () => {
        setStatus('Error al cargar prompts.');
        setStatusType('error');
      }
    );

    return () => unsubscribe();
  }, [activePath]);

  useEffect(() => {
    const previewPromptsRef = ref(database, scopePathMap.preview);
    const unsubscribe = onValue(
      previewPromptsRef,
      (snapshot) => {
        const value = snapshot.val() as Record<string, PromptTemplate> | null;
        setPreviewTemplates(parseRtdbTemplates(value));
      },
      () => {
        setPreviewTemplates([]);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const standardPromptsRef = ref(database, scopePathMap.standard);
    const unsubscribe = onValue(
      standardPromptsRef,
      (snapshot) => {
        const value = snapshot.val() as Record<string, PromptTemplate> | null;
        setStandardTemplates(parseRtdbTemplates(value));
      },
      () => {
        setStandardTemplates([]);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const globalPromptsRef = ref(database, scopePathMap.global);
    const unsubscribe = onValue(
      globalPromptsRef,
      (snapshot) => {
        const value = snapshot.val() as Record<string, PromptTemplate> | null;
        setGlobalTemplates(parseRtdbTemplates(value));
      },
      () => {
        setGlobalTemplates([]);
      }
    );

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (statusTimer.current) {
      window.clearTimeout(statusTimer.current);
      statusTimer.current = null;
    }

    if (!status) {
      return;
    }

    if (status === 'Guardando...' || status === 'Eliminando...') {
      return;
    }

    statusTimer.current = window.setTimeout(() => {
      setStatus('');
      setStatusType('neutral');
    }, 2600);

    return () => {
      if (statusTimer.current) {
        window.clearTimeout(statusTimer.current);
        statusTimer.current = null;
      }
    };
  }, [status]);

  const isImageModel = useMemo(
    () => draft.model.toLowerCase().includes('image'),
    [draft.model]
  );

  const extractedInputs = useMemo(
    () => extractPromptVariables(draft.prompt),
    [draft.prompt]
  );

  const fileExtension = isImageModel ? 'png' : 'json';
  const archivoCompleto = draft.archivoBase ? `${draft.archivoBase}.${fileExtension}` : '';
  const normalizedTemplateInputs = useMemo(
    () =>
      templates.map((template) =>
        new Set(
          [...template.inputs, ...template.inputs_img]
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
        )
      ),
    [templates]
  );
  const normalizedDisplayedOutputs = useMemo(
    () =>
      templates.map((template) =>
        new Set(
          (template.archivo.toLowerCase().endsWith('.png')
            ? [...template.outputs, template.archivo]
            : template.outputs
          )
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
        )
      ),
    [templates]
  );
  const normalizedTemplateOutputLinks = useMemo(
    () =>
      templates.map((template) =>
        new Set(
          [...template.outputs, template.archivo]
            .map((item) => item.trim())
            .filter((item) => item.length > 0)
        )
      ),
    [templates]
  );
  const outputPromptCountMap = useMemo(() => {
    const countMap = new Map<string, number>();
    normalizedDisplayedOutputs.forEach((outputSet) => {
      outputSet.forEach((item) => {
        countMap.set(item, (countMap.get(item) || 0) + 1);
      });
    });
    return countMap;
  }, [normalizedDisplayedOutputs]);
  const previewOutputsAsInputs = useMemo(() => {
    const result = new Set<string>();
    previewTemplates.forEach((template) => {
      const outputsToInclude = template.archivo.toLowerCase().endsWith('.png')
        ? [...template.outputs, template.archivo]
        : template.outputs;
      outputsToInclude
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
        .forEach((item) => result.add(item));
    });
    return Array.from(result);
  }, [previewTemplates]);
  const flowInputItems = useMemo(
    () =>
      scope === 'preview'
        ? flowQuestionItems
        : [...flowQuestionItems, ...previewOutputsAsInputs],
    [scope, previewOutputsAsInputs]
  );
  const flowInputSet = useMemo(
    () => new Set(flowInputItems.map((item) => item.trim()).filter((item) => item.length > 0)),
    [flowInputItems]
  );
  const flowOutputsFromTemplates = useMemo(() => {
    const result = new Set<string>();
    templates.forEach((template) => {
      if (!template.is_pdf && !template.is_logo) {
        return;
      }
      const outputsToShow = template.archivo.toLowerCase().endsWith('.png')
        ? [...template.outputs, template.archivo]
        : template.outputs;
      outputsToShow
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
        .forEach((item) => result.add(item));
    });
    return result;
  }, [templates]);
  const flowOutputItems = useMemo(
    () =>
      Array.from(
        new Set([
          ...Array.from(flowOutputsFromTemplates),
          ...hardcodedFlowOutputsByScope[scope]
            .map((item) => item.trim())
            .filter((item) => item.length > 0),
        ])
      ),
    [flowOutputsFromTemplates, scope]
  );
  const flowOutputSet = useMemo(() => new Set(flowOutputItems), [flowOutputItems]);
  const availableModelSet = useMemo(
    () => new Set(availableModels.map((item) => item.trim()).filter((item) => item.length > 0)),
    [availableModels]
  );
  const flowOutputTypeMap = useMemo(() => {
    const typeMap = new Map<string, { isLogo: boolean; isPdf: boolean }>();
    templates.forEach((template) => {
      const outputsToShow = template.archivo.toLowerCase().endsWith('.png')
        ? [...template.outputs, template.archivo]
        : template.outputs;
      outputsToShow
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
        .forEach((item) => {
          const previous = typeMap.get(item) || { isLogo: false, isPdf: false };
          typeMap.set(item, {
            isLogo: previous.isLogo || Boolean(template.is_logo),
            isPdf: previous.isPdf || Boolean(template.is_pdf),
          });
        });
    });
    return typeMap;
  }, [templates]);
  const allPromptInputSet = useMemo(() => {
    const result = new Set<string>();
    normalizedTemplateInputs.forEach((inputSet) => {
      inputSet.forEach((item) => result.add(item));
    });
    return result;
  }, [normalizedTemplateInputs]);
  const fullPromptInputSet = useMemo(() => {
    const result = new Set<string>();
    [...standardTemplates, ...globalTemplates].forEach((template) => {
      [...template.inputs, ...template.inputs_img]
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
        .forEach((item) => result.add(item));
    });
    return result;
  }, [standardTemplates, globalTemplates]);
  const scopedBlueUsageState = useMemo(() => buildBlueUsageState(templates), [templates]);
  const standardBlueUsageState = useMemo(
    () => buildBlueUsageState(standardTemplates),
    [standardTemplates]
  );
  const globalBlueUsageState = useMemo(
    () => buildBlueUsageState(globalTemplates),
    [globalTemplates]
  );
  const allPromptOutputSet = useMemo(() => {
    const result = new Set<string>();
    templates.forEach((template) => {
      const outputsToShow = template.archivo.toLowerCase().endsWith('.png')
        ? [...template.outputs, template.archivo]
        : template.outputs;
      outputsToShow
        .map((item) => item.trim())
        .filter((item) => item.length > 0)
        .forEach((item) => result.add(item));
    });
    return result;
  }, [templates]);

  const resetStatus = () => {
    setStatus('');
    setStatusType('neutral');
  };

  const openCreateEditor = (index: number) => {
    setEditor({ mode: 'create', index });
    setDraft({
      nombre: '',
      archivoBase: '',
      model: availableModels[0] ?? '',
      thinkingLevel: 'default',
      enableWebSearch: false,
      imageScale: '1:1',
      imageResolution: '1K',
      isPdf: false,
      isLogo: false,
      prompt: '',
      inputs_img: [],
      outputs: [],
    });
    setListInputImgDraft('');
    setListOutputDraft('');
    resetStatus();
  };

  const openEditEditor = (index: number) => {
    const current = templates[index];
    if (!current) {
      return;
    }

    const archivoBase = (current.archivo || '').replace(/\.(json|png)$/i, '');
    setEditor({ mode: 'edit', index });
    setDraft({
      nombre: current.nombre,
      archivoBase,
      model: current.model,
      thinkingLevel: current.thinking_level ?? 'default',
      enableWebSearch: Boolean(current.enable_web_search),
      imageScale: current.image_scale ?? '1:1',
      imageResolution: current.image_resolution ?? '1K',
      isPdf: Boolean(current.is_pdf),
      isLogo: Boolean(current.is_logo),
      prompt: current.prompt,
      inputs_img: [...current.inputs_img],
      outputs: [...current.outputs],
    });
    setListInputImgDraft('');
    setListOutputDraft('');
    resetStatus();
  };

  const closeEditor = () => {
    setEditor(null);
    setListInputImgDraft('');
    setListOutputDraft('');
  };

  const reindexTemplates = (items: PromptTemplate[]) => {
    return items.map((item, index) => ({
      ...item,
      id: index + 1,
    }));
  };

  const persistTemplates = async (items: PromptTemplate[]) => {
    const reordered = reindexTemplates(items);
    if (reordered.length === 0) {
      await set(ref(database, activePath), null);
      return;
    }
    const payload = Object.fromEntries(
      reordered.map((item) => {
        const cleanItem: PromptTemplate = {
          id: item.id,
          nombre: item.nombre,
          archivo: item.archivo,
          model: item.model,
          thinking_level: item.thinking_level ?? null,
          ...(!item.model.toLowerCase().includes('image')
            ? { enable_web_search: Boolean(item.enable_web_search) }
            : {}),
          is_pdf: Boolean(item.is_pdf),
          is_logo: Boolean(item.is_logo),
          inputs: item.inputs ?? [],
          outputs: item.outputs ?? [],
          inputs_img: item.inputs_img ?? [],
          prompt: item.prompt ?? '',
          ...(item.image_scale ? { image_scale: item.image_scale } : {}),
          ...(item.image_resolution ? { image_resolution: item.image_resolution } : {}),
        };
        return [String(item.id), cleanItem];
      })
    );
    await set(ref(database, activePath), payload);
  };

  const handleAddToList = (type: 'inputs_img' | 'outputs') => {
    const value = (type === 'inputs_img' ? listInputImgDraft : listOutputDraft).trim();
    if (!value) {
      return;
    }

    if (type === 'inputs_img') {
      setDraft((prev) => ({ ...prev, inputs_img: [...prev.inputs_img, value] }));
      setListInputImgDraft('');
    } else {
      setDraft((prev) => ({ ...prev, outputs: [...prev.outputs, value] }));
      setListOutputDraft('');
    }
  };

  const handleRemoveFromList = (type: 'inputs_img' | 'outputs', indexToRemove: number) => {
    if (type === 'inputs_img') {
      setDraft((prev) => ({
        ...prev,
        inputs_img: prev.inputs_img.filter((_, index) => index !== indexToRemove),
      }));
    } else {
      setDraft((prev) => ({
        ...prev,
        outputs: prev.outputs.filter((_, index) => index !== indexToRemove),
      }));
    }
  };

  const handleSaveEditor = async () => {
    if (!editor) {
      return;
    }

    if (!draft.archivoBase) {
      setStatus('Archivo inválido. Usa solo minusculas, numeros y guion bajo.');
      setStatusType('error');
      return;
    }
    if (reservedFileNames.has(`${draft.archivoBase}.${fileExtension}`.toLowerCase())) {
      setStatus('Ese nombre de archivo está reservado por el sistema.');
      setStatusType('error');
      return;
    }

    setIsSaving(true);
    setStatus('Guardando...');
    setStatusType('neutral');

    const nextTemplate: PromptTemplate = {
      id: 0,
      nombre: draft.nombre,
      archivo: `${draft.archivoBase}.${fileExtension}`,
      model: draft.model,
      thinking_level: draft.thinkingLevel === 'default' ? null : draft.thinkingLevel,
      ...(isImageModel
        ? {
            image_scale: draft.imageScale,
            image_resolution: draft.imageResolution,
          }
        : {
            enable_web_search: draft.enableWebSearch,
          }),
      is_pdf: draft.isPdf,
      is_logo: draft.isLogo,
      inputs: extractedInputs,
      outputs: isImageModel ? [] : draft.outputs,
      inputs_img: draft.inputs_img,
      prompt: draft.prompt,
    };

    try {
      if (editor.mode === 'create') {
        const next = [...templates];
        next.splice(editor.index, 0, nextTemplate);
        await persistTemplates(next);
        setStatus('Prompt creado.');
      } else {
        const next = [...templates];
        next[editor.index] = nextTemplate;
        await persistTemplates(next);
        setStatus('Prompt actualizado.');
      }
      setStatusType('success');
      closeEditor();
    } catch (error) {
      setStatus('Error al guardar el prompt.');
      setStatusType('error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteTemplate = async (index: number) => {
    const current = templates[index];
    if (!current) {
      return;
    }

    const confirmed = window.confirm(`¿Eliminar el prompt ${current.id} (${current.nombre || 'sin nombre'})?`);
    if (!confirmed) {
      return;
    }

    setStatus('Eliminando...');
    setStatusType('neutral');

    try {
      const next = templates.filter((_, itemIndex) => itemIndex !== index);
      await persistTemplates(next);
      setStatus('Prompt eliminado.');
      setStatusType('success');
    } catch (error) {
      setStatus('Error al eliminar el prompt.');
      setStatusType('error');
    }
  };

  const renderInsertButton = (index: number) => (
    <div className="brand-insert-slot" key={`insert-${index}`}>
      <button
        className="secondary-button"
        type="button"
        onClick={() => openCreateEditor(index)}
      >
        +
      </button>
    </div>
  );

  const isInputMissingInOtherOutputs = (templateIndex: number, rawValue: string) => {
    const value = rawValue.trim();
    if (!value) {
      return false;
    }
    if (flowInputSet.has(value)) {
      return false;
    }
    for (let index = 0; index < normalizedTemplateOutputLinks.length; index += 1) {
      if (index === templateIndex) {
        continue;
      }
      if (normalizedTemplateOutputLinks[index].has(value)) {
        return false;
      }
    }
    return true;
  };

  const isOutputMissingInOtherInputs = (templateIndex: number, rawValue: string) => {
    const value = rawValue.trim();
    if (!value) {
      return false;
    }
    if (flowOutputSet.has(value)) {
      return false;
    }
    if (scope === 'preview' && fullPromptInputSet.has(value)) {
      return false;
    }
    for (let index = 0; index < normalizedTemplateInputs.length; index += 1) {
      if (index === templateIndex) {
        continue;
      }
      if (normalizedTemplateInputs[index].has(value)) {
        return false;
      }
    }
    return true;
  };

  const isOutputRepeatedAcrossPrompts = (rawValue: string) => {
    const value = rawValue.trim();
    if (!value) {
      return false;
    }
    return (outputPromptCountMap.get(value) || 0) > 1;
  };

  const isFlowInputUsed = (rawValue: string) => {
    const value = rawValue.trim();
    if (!value) {
      return false;
    }
    return allPromptInputSet.has(value);
  };

  const isFlowOutputUsed = (rawValue: string) => {
    const value = rawValue.trim();
    if (!value) {
      return false;
    }
    return allPromptOutputSet.has(value) || flowInputSet.has(value);
  };

  const isUsedOnlyByBlueOutputPrompts = (
    rawValue: string,
    blueUsageStates: BlueUsageState[],
    excludedTemplateIndex?: number
  ) => {
    const value = rawValue.trim();
    let hasConsumer = false;
    for (const blueUsageState of blueUsageStates) {
      const consumers = blueUsageState.consumerMap.get(value);
      if (!consumers) {
        continue;
      }
      for (const index of Array.from(consumers)) {
        if (index === excludedTemplateIndex) {
          continue;
        }
        hasConsumer = true;
        if (!blueUsageState.blueOnlyTemplateMap[index]) {
          return false;
        }
      }
    }
    return hasConsumer;
  };

  const isFlowInputBlue = (rawValue: string) =>
    scope !== 'preview' &&
    isUsedOnlyByBlueOutputPrompts(rawValue, [scopedBlueUsageState]);

  const isPromptOutputBlue = (templateIndex: number, rawValue: string) => {
    const value = rawValue.trim();
    if (scope === 'preview') {
      return isUsedOnlyByBlueOutputPrompts(value, [
        standardBlueUsageState,
        globalBlueUsageState,
      ]);
    }
    const consumers = scopedBlueUsageState.consumerMap.get(value);
    const isUsedAsInput = Boolean(
      consumers && Array.from(consumers).some((index) => index !== templateIndex)
    );
    if (isUsedAsInput) {
      return isUsedOnlyByBlueOutputPrompts(
        value,
        [scopedBlueUsageState],
        templateIndex
      );
    }
    return pdfOnlyFlowItems.has(value);
  };

  const getFlowOutputClassName = (rawValue: string) => {
    const value = rawValue.trim();
    if (scope !== 'preview') {
      const outputType = flowOutputTypeMap.get(value);
      if (
        pdfOnlyFlowItems.has(value) &&
        !outputType?.isLogo &&
        !outputType?.isPdf
      ) {
        return 'brand-item-blue';
      }
    }
    if (!isFlowOutputUsed(value)) {
      return 'brand-item-red';
    }
    return 'brand-item-green';
  };

  return (
    <div className="section">
      <div className="section-header">
        <h2>Generación de marca</h2>
      </div>
      <div className="prompt-result-card">
        <div>
          En <span className="brand-item-green">verde</span> se colorea lo que ya esta bien.
        </div>
        <div>
          En <span className="brand-item-yellow">amarillo</span> se colorean los outputs repetidos.
          No es error, pero quiere decir que se genera lo mismo en dos llamadas a Gemini.
        </div>
        <div>
          En <span className="brand-item-red">rojo</span> se colorean los inputs que no se han
          definido antes o los outputs que no se usan.
        </div>
        <div>
          En <span className="brand-item-blue">azul</span> se colorean los inputs/outputs que se
          usan exclusivamente para escribir en los PDFs generados (no generados con IA).
        </div>
      </div>

      <div className="panel-actions">
        <button
          className={`ghost-button ${scope === 'preview' ? 'ghost-button-active' : ''}`}
          type="button"
          onClick={() => {
            setScope('preview');
            closeEditor();
          }}
        >
          Preview
        </button>
        <button
          className={`ghost-button ${scope === 'standard' ? 'ghost-button-active' : ''}`}
          type="button"
          onClick={() => {
            setScope('standard');
            closeEditor();
          }}
        >
          Standard
        </button>
        <button
          className={`ghost-button ${scope === 'global' ? 'ghost-button-active' : ''}`}
          type="button"
          onClick={() => {
            setScope('global');
            closeEditor();
          }}
        >
          Global
        </button>
      </div>
      <div className="brand-section-divider" />

      {modelsStatus ? <div className="hint">{modelsStatus}</div> : null}

      {status ? <div className={`inline-toast inline-toast-${statusType}`}>{status}</div> : null}

      {editor ? (
        <div className="prompt-ready">
          <div className="section-header">
            <h3>
              {editor.mode === 'create'
                ? `Nuevo prompt (${scopeLabelMap[scope]})`
                : `Editar prompt ${templates[editor.index]?.id || ''} (${scopeLabelMap[scope]})`}
            </h3>
            <div className="panel-actions-inline">
              <button className="secondary-button" type="button" onClick={closeEditor}>
                Cancelar
              </button>
              <button className="primary-button" type="button" onClick={handleSaveEditor} disabled={isSaving}>
                {isSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </div>

          <label className="field">
            <span>ID</span>
            <input
              className="table-input"
              type="text"
              readOnly
              value={editor.mode === 'create' ? editor.index + 1 : templates[editor.index]?.id || ''}
            />
          </label>

          <label className="field">
            <span>Nombre</span>
            <input
              className="table-input"
              type="text"
              value={draft.nombre}
              onChange={(event) => setDraft((prev) => ({ ...prev, nombre: event.target.value }))}
              placeholder="Nombre del prompt"
            />
          </label>

          <label className="field">
            <span>Modelo de IA</span>
            <select
              className="table-input"
              value={draft.model}
              onChange={(event) => setDraft((prev) => ({ ...prev, model: event.target.value }))}
              disabled={!availableModels.length}
            >
              {availableModels.length ? null : <option value="">{modelsStatus || 'Sin modelos'}</option>}
              {availableModels.map((modelId) => (
                <option key={modelId} value={modelId}>
                  {modelId}
                </option>
              ))}
            </select>
          </label>
          <label className="field">
            <span>Thinking level</span>
            <select
              className="table-input"
              value={draft.thinkingLevel}
              onChange={(event) =>
                setDraft((prev) => ({
                  ...prev,
                  thinkingLevel: event.target.value as ThinkingLevelOption,
                }))
              }
            >
              <option value="default">Default</option>
              {thinkingLevelOptions.map((level) => (
                <option key={level} value={level}>
                  {level}
                </option>
              ))}
            </select>
          </label>
          {!isImageModel ? (
            <label className="field">
              <span>Enable web search</span>
              <input
                type="checkbox"
                checked={draft.enableWebSearch}
                onChange={(event) =>
                  setDraft((prev) => ({ ...prev, enableWebSearch: event.target.checked }))
                }
              />
            </label>
          ) : null}

          <label className="field">
            <span>Prompt</span>
            <textarea
              className="table-input prompt-tester-textarea"
              value={draft.prompt}
              onChange={(event) => setDraft((prev) => ({ ...prev, prompt: event.target.value }))}
            />
          </label>

          <div className="field">
            <span>Inputs</span>
            {extractedInputs.length ? (
              <div className="table table-compact">
                <div className="table-row table-header brand-list-row">
                  <div>Valor</div>
                </div>
                {extractedInputs.map((item, index) => (
                  <div className="table-row brand-list-row" key={`${item}-${index}`}>
                    <div className="mono">{item}</div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="hint">Sin variables detectadas.</div>
            )}
          </div>

          <div className="field">
            <span>Imágenes</span>
            <div className="project-search">
              <input
                type="text"
                value={listInputImgDraft}
                onChange={(event) => setListInputImgDraft(event.target.value)}
                placeholder="Agregar input_img"
              />
              <button className="secondary-button" type="button" onClick={() => handleAddToList('inputs_img')}>
                Agregar
              </button>
            </div>
          </div>
          {draft.inputs_img.length ? (
            <div className="table table-compact">
              <div className="table-row table-header brand-list-row-with-action">
                <div>Valor</div>
                <div>Acción</div>
              </div>
              {draft.inputs_img.map((item, index) => (
                <div className="table-row brand-list-row-with-action" key={`${item}-${index}`}>
                  <div className="mono">{item}</div>
                  <div className="table-action">
                    <button
                      className="secondary-button"
                      type="button"
                      onClick={() => handleRemoveFromList('inputs_img', index)}
                    >
                      Quitar
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : null}

          <label className="field">
            <span>Nombre archivo</span>
            <input
              className="table-input mono"
              type="text"
              value={draft.archivoBase}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, archivoBase: sanitizeArchivoBase(event.target.value) }))
              }
              placeholder="solo_minusculas_y_numeros"
            />
          </label>
          <div className="hint">Archivo final: {archivoCompleto || `.${fileExtension}`}</div>

          {isImageModel ? (
            <>
              <label className="field">
                <span>Escala</span>
                <select
                  className="table-input"
                  value={draft.imageScale}
                  onChange={(event) =>
                    setDraft((prev) => ({ ...prev, imageScale: event.target.value as ImageScale }))
                  }
                >
                  {imageScaleOptions.map((scale) => (
                    <option key={scale} value={scale}>
                      {scale}
                    </option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Resolución</span>
                <select
                  className="table-input"
                  value={draft.imageResolution}
                  onChange={(event) =>
                    setDraft((prev) => ({
                      ...prev,
                      imageResolution: event.target.value as ImageResolution,
                    }))
                  }
                >
                  {imageResolutionOptions.map((resolution) => (
                    <option key={resolution} value={resolution}>
                      {resolution}
                    </option>
                  ))}
                </select>
              </label>
            </>
          ) : null}

          {isImageModel ? (
            <>
              <label className="field">
                <span>Es PDF</span>
                <input
                  type="checkbox"
                  checked={draft.isPdf}
                  onChange={(event) => setDraft((prev) => ({ ...prev, isPdf: event.target.checked }))}
                />
              </label>
              <label className="field">
                <span>Es Logo</span>
                <input
                  type="checkbox"
                  checked={draft.isLogo}
                  onChange={(event) => setDraft((prev) => ({ ...prev, isLogo: event.target.checked }))}
                />
              </label>
            </>
          ) : (
            <>
              <div className="field">
                <span>Salida del prompt</span>
                <div className="project-search">
                  <input
                    type="text"
                    value={listOutputDraft}
                    onChange={(event) => setListOutputDraft(event.target.value)}
                    placeholder="Agregar output"
                  />
                  <button
                    className="secondary-button"
                    type="button"
                    onClick={() => handleAddToList('outputs')}
                  >
                    Agregar
                  </button>
                </div>
              </div>
              {draft.outputs.length ? (
                <div className="table table-compact">
                  <div className="table-row table-header brand-list-row-with-action">
                    <div>Valor</div>
                    <div>Acción</div>
                  </div>
                  {draft.outputs.map((item, index) => (
                    <div className="table-row brand-list-row-with-action" key={`${item}-${index}`}>
                      <div className="mono">{item}</div>
                      <div className="table-action">
                        <button
                          className="secondary-button"
                          type="button"
                          onClick={() => handleRemoveFromList('outputs', index)}
                        >
                          Quitar
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </>
          )}
        </div>
      ) : (
        <div className="brand-prompts-stack">
          <div className="brand-prompt-card">
            <div className="message-label">Inputs del flujo</div>
            <ul>
              {flowInputItems.map((item) => (
                <li
                  className={`mono ${
                    isFlowInputBlue(item)
                      ? 'brand-item-blue'
                      : isFlowInputUsed(item)
                        ? 'brand-item-green'
                        : 'brand-item-red'
                  }`}
                  key={item}
                >
                  {item}
                </li>
              ))}
            </ul>
          </div>
          {renderInsertButton(0)}
          {templates.map((template, index) => {
            const mergedInputs = [...template.inputs, ...template.inputs_img];
            const outputsToShow = template.archivo.toLowerCase().endsWith('.png')
              ? [...template.outputs, template.archivo]
              : template.outputs;

            return (
              <React.Fragment key={template.id}>
                <div className="brand-prompt-view">
                  <div className="brand-prompt-list">
                    <div className="message-label">inputs</div>
                    {mergedInputs.length ? (
                      <ul>
                        {mergedInputs.map((item, itemIndex) => (
                          <li
                            className={`mono ${
                              isFlowInputBlue(item)
                                ? 'brand-item-blue'
                                : isInputMissingInOtherOutputs(index, item)
                                  ? 'brand-item-red'
                                  : 'brand-item-green'
                            }`}
                            key={`${item}-${itemIndex}`}
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="hint">Sin inputs</div>
                    )}
                  </div>

                  <div className="brand-prompt-card">
                    <div className="section-header">
                      <h3>{`${template.id}: ${template.nombre || '-'}`}</h3>
                      <div className="panel-actions-inline">
                        <button
                          className="secondary-button icon-button"
                          type="button"
                          title="Editar"
                          aria-label="Editar"
                          onClick={() => openEditEditor(index)}
                        >
                          ✎
                        </button>
                        <button
                          className="secondary-button icon-button"
                          type="button"
                          title="Eliminar"
                          aria-label="Eliminar"
                          onClick={() => handleDeleteTemplate(index)}
                        >
                          🗑
                        </button>
                      </div>
                    </div>
                    <div className="table table-compact">
                      <div className="table-row brand-list-row">
                        <div>
                          <div className="message-label">archivo</div>
                          <div className="mono">{template.archivo || '-'}</div>
                        </div>
                      </div>
                      <div className="table-row brand-list-row">
                        <div>
                          <div className="message-label">model</div>
                          <div
                            className={`mono ${
                              template.model.trim().length > 0 &&
                              !availableModelSet.has(template.model.trim())
                                ? 'brand-item-red'
                                : ''
                            }`}
                          >
                            {template.model || '-'}
                          </div>
                        </div>
                      </div>
                      <div className="table-row brand-list-row">
                        <div>
                          <div className="message-label">Thinking level</div>
                          <div className="mono">{template.thinking_level || 'Default'}</div>
                        </div>
                      </div>
                      {!template.model.toLowerCase().includes('image') ? (
                        <div className="table-row brand-list-row">
                          <div>
                            <div className="message-label">Enable web search</div>
                            <div className="mono">{template.enable_web_search ? 'Sí' : 'No'}</div>
                          </div>
                        </div>
                      ) : null}
                      {template.is_logo || template.is_pdf ? (
                        <div className="table-row brand-list-row">
                          <div>
                            <div className="message-label">tipo</div>
                            <div className="mono">
                              {[template.is_logo ? 'Logo' : '', template.is_pdf ? 'PDF' : '']
                                .filter(Boolean)
                                .join(' | ')}
                            </div>
                          </div>
                        </div>
                      ) : null}
                    </div>
                  </div>

                  <div className="brand-prompt-list">
                    <div className="message-label">outputs</div>
                    {template.archivo.toLowerCase().endsWith('.png') ? (
                      <div className="panel-actions-inline">
                        {template.image_scale ? (
                          <span className="status">{template.image_scale}</span>
                        ) : null}
                        {template.image_resolution ? (
                          <span className="status">{template.image_resolution}</span>
                        ) : null}
                      </div>
                    ) : null}
                    {outputsToShow.length ? (
                      <ul>
                        {outputsToShow.map((item, itemIndex) => (
                          <li
                            className={`mono ${
                              isOutputRepeatedAcrossPrompts(item)
                                ? 'brand-item-yellow'
                                : isPromptOutputBlue(index, item)
                                  ? 'brand-item-blue'
                                  : isOutputMissingInOtherInputs(index, item)
                                    ? 'brand-item-red'
                                    : 'brand-item-green'
                            }`}
                            key={`${item}-${itemIndex}`}
                          >
                            {item}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <div className="hint">Sin outputs</div>
                    )}
                  </div>
                </div>
                {renderInsertButton(index + 1)}
              </React.Fragment>
            );
          })}
          <div className="brand-prompt-card">
            <div className="message-label">Outputs del flujo</div>
            {flowOutputItems.length ? (
              <ul>
                {flowOutputItems.map((item) => (
                  <li
                    className={`mono ${getFlowOutputClassName(item)}`}
                    key={item}
                  >
                    <span>{item}</span>
                    {flowOutputTypeMap.get(item)?.isLogo || flowOutputTypeMap.get(item)?.isPdf ? (
                      <span className="brand-inline-tags">
                        {flowOutputTypeMap.get(item)?.isLogo ? <span className="status">Logo</span> : null}
                        {flowOutputTypeMap.get(item)?.isPdf ? <span className="status">PDF</span> : null}
                      </span>
                    ) : null}
                    {scope !== 'preview' && pdfOnlyFlowItems.has(item) ? (
                      <span className="brand-inline-tags">
                        <span className="status source-pill-forced">PDF NO IA</span>
                      </span>
                    ) : null}
                  </li>
                ))}
              </ul>
            ) : (
              <div className="hint">Sin outputs del flujo.</div>
            )}
          </div>
          {!templates.length ? <div className="hint">Sin prompts en {scopeLabelMap[scope]}.</div> : null}
        </div>
      )}
    </div>
  );
}

export default BrandGenerationSection;
